import { storeToRefs } from 'pinia'
import { useI18n } from 'vue-i18n'

import useMoment from '@/composables/useMoment'

import { useCompanyStore } from '@/stores/companyStore'

export default function useFormatters() {
	const { t: $t, locale } = useI18n()

	const companyStore = useCompanyStore()
	const {
		currentCompany,
	} = storeToRefs(companyStore)

	const {
		formatToFriendlyDateTime,
		formatToFriendlyPeriod,
		makeDurationFormatterFromSeconds,
	} = useMoment()

	const getLocales = () => {
		const locales = []
		if (currentCompany.value.country && locale.value) {
			locales.push(`${locale.value}-${currentCompany.value.country}`, locale.value, 'ru-RU')
		} else if (locale.value) {
			locales.push(locale.value, 'ru-RU')
		} else {
			locales.push('ru-RU')
		}
		return locales
	}

	const formatDefaultNumber = (value, overriddenParams) => {
		const params = Object.assign({
			minimumFractionDigits: 0,
			maximumFractionDigits: 2,
			valueFormatterSuffix: null,
			returnNumber: false,
		}, overriddenParams)

		const locales = getLocales()

		if (params.returnNumber) {
			const number = Number(value).toFixed(params.maximumFractionDigits)
			return parseFloat(number)
		} else {
			const number = Number(value).toLocaleString(locales, {
				minimumFractionDigits: params.minimumFractionDigits,
				maximumFractionDigits: params.maximumFractionDigits,
			})

			let formattedNumber = params.valueFormatterSuffix ? `${number} ${params.valueFormatterSuffix}` : number

			return formattedNumber
		}
	}

	/**
	 * formatCurrency
	 * @param {number} value amount
	 * @param {object} overriddenParams options
	 * @param {boolean} overriddenParams.showFractions do I need to show the fractional parts
	 * @returns {string}
	 */
	const formatCurrency = (value, overriddenParams = {}) => {
		const options = Object.assign({
			showFractions: true,
		}, overriddenParams)

		const locales = getLocales()

		const currency = currentCompany.value.currency

		const fractionsLength = options.showFractions ? 2 : 0

		return Number(value).toLocaleString(locales, currency ? {
			style: 'currency',
			currency: currency,
			minimumFractionDigits: fractionsLength,
			maximumFractionDigits: fractionsLength,
		} : {
			style: 'decimal',
			minimumFractionDigits: fractionsLength,
			maximumFractionDigits: fractionsLength,
		})
	}

	/**
	 * formatPercent
	 * @param {number} value rawValue
	 * @param {object} overriddenParams options
	 * @param {number} overriddenParams.minimumFractionDigits minimumFractionDigits = 0
	 * @param {number} overriddenParams.maximumFractionDigits maximumFractionDigits = 2
	 * @param {boolean} overriddenParams.isCoefficient row value is coefficient
	 * @returns {string}
	 */
	const formatPercent = (value, overriddenParams) => {
		const params = Object.assign({
			minimumFractionDigits: 0,
			maximumFractionDigits: 2,
			isCoefficient: true,
		}, overriddenParams)

		const locales = getLocales()

		const number = params.isCoefficient ? Number(value) : Number(value) / 100

		return number.toLocaleString(locales, {
			style: 'percent',
			minimumFractionDigits: params.minimumFractionDigits,
			maximumFractionDigits: params.maximumFractionDigits,
		})
	}

	const formatPercentForPreview = (value, overriddenParams) => {
		const params = Object.assign({
			minimumFractionDigits: 0,
			maximumFractionDigits: 2,
			isCoefficient: true,
		}, overriddenParams)

		return formatPercent(value, params)
	}

	/**
	 * Area formatter
	 * @param {number} value area value
	 * @param {object} overriddenParams params
	 * @param {number} overriddenParams.minimumFractionDigits minimumFractionDigits (2)
	 * @param {number} overriddenParams.maximumFractionDigits maximumFractionDigits (2)
	 * @param {string} overriddenParams.valueFormatterSuffix valueFormatterSuffix ($t('ha'))
	 * @returns {string}
	 */
	const formatArea = (value, overriddenParams) => {
		if (value === null) return null

		const params = Object.assign({
			minimumFractionDigits: 2,
			maximumFractionDigits: 2,
			valueFormatterSuffix: $t('ha'),
			// isCoefficient: true,
		}, overriddenParams)

		const locales = getLocales()

		const area = Number(value).toLocaleString(locales, {
			minimumFractionDigits: params.minimumFractionDigits,
			maximumFractionDigits: params.maximumFractionDigits,
		})

		let formattedArea = `${area} ${params.valueFormatterSuffix}`

		return formattedArea
	}

	const formatAreaForPreview = (value, overriddenParams) => {
		const params = Object.assign({
			minimumFractionDigits: 2,
			maximumFractionDigits: 2,
			valueFormatterSuffix: $t('ha'),
		}, overriddenParams)

		return formatArea(value, params)
	}

	/**
	 * Distance formatter
	 * @param {number} value distance value
	 * @param {object} overriddenParams params
	 * @param {number} overriddenParams.minimumFractionDigits minimumFractionDigits (2)
	 * @param {number} overriddenParams.maximumFractionDigits maximumFractionDigits (2)
	 * @param {string} overriddenParams.valueFormatterSuffix valueFormatterSuffix ($t('ha'))
	 * @returns {string}
	 */
	const formatDistance = (value, overriddenParams) => {
		const params = Object.assign({
			minimumFractionDigits: 0,
			maximumFractionDigits: 0,
			valueFormatterSuffix: $t('km'),
		}, overriddenParams)

		const locales = getLocales()

		const distance = Number(value).toLocaleString(locales, {
			minimumFractionDigits: params.minimumFractionDigits,
			maximumFractionDigits: params.maximumFractionDigits,
		})

		return params.valueFormatterSuffix ? `${distance} ${params.valueFormatterSuffix}` : distance
	}

	/**
	 * Weight formatter
	 * @param {number} value weight value
	 * @param {object} overriddenParams params
	 * @param {number} overriddenParams.minimumFractionDigits minimumFractionDigits (2)
	 * @param {number} overriddenParams.maximumFractionDigits maximumFractionDigits (2)
	 * @param {string} overriddenParams.valueFormatterSuffix valueFormatterSuffix ($t('ha'))
	 * @returns {string}
	 */
	const formatWeight = (value, overriddenParams) => {
		const params = Object.assign({
			minimumFractionDigits: 0,
			maximumFractionDigits: 2,
			valueFormatterSuffix: $t('t'),
		}, overriddenParams)

		const locales = getLocales()

		const weight = Number(value).toLocaleString(locales, {
			minimumFractionDigits: params.minimumFractionDigits,
			maximumFractionDigits: params.maximumFractionDigits,
		})

		return `${weight} ${params.valueFormatterSuffix}`
	}

	/**
	 * Weight formatter for preview
	 * @param {number} value weight value
	 * @param {object} overriddenParams params
	 * @param {number} overriddenParams.minimumFractionDigits minimumFractionDigits (2)
	 * @param {number} overriddenParams.maximumFractionDigits maximumFractionDigits (2)
	 * @param {string} overriddenParams.valueFormatterSuffix valueFormatterSuffix ($t('ha'))
	 * @returns {string}
	 */
	const formatWeightForPreview = (value, overriddenParams) => {
		const params = Object.assign({
			minimumFractionDigits: 2,
			maximumFractionDigits: 3,
		}, overriddenParams)

		return formatWeight(value, params)
	}

	/**
	 * Weight*Distance formatter
	 * @param {number} value weight*distance value
	 * @param {object} overriddenParams params
	 * @param {number} overriddenParams.minimumFractionDigits minimumFractionDigits (2)
	 * @param {number} overriddenParams.maximumFractionDigits maximumFractionDigits (2)
	 * @param {string} overriddenParams.valueFormatterSuffix valueFormatterSuffix ($t('t*km'))
	 * @returns {string}
	 */
	const formatWeightDistance = (value, overriddenParams) => {
		const params = Object.assign({
			minimumFractionDigits: 0,
			maximumFractionDigits: 2,
			valueFormatterSuffix: $t('t*km'),
		}, overriddenParams)

		const locales = getLocales()

		const weightDistance = Number(value).toLocaleString(locales, {
			minimumFractionDigits: params.minimumFractionDigits,
			maximumFractionDigits: params.maximumFractionDigits,
		})

		return `${weightDistance} ${params.valueFormatterSuffix}`
	}

	const formatQuantity = (value, overriddenParams) => {
		const params = Object.assign({
			minimumFractionDigits: 0,
			maximumFractionDigits: 2,
			valueFormatterSuffix: null,
			usePluralRule: false,
		}, overriddenParams)

		const locales = getLocales()

		const quantity = Number(value).toLocaleString(locales, {
			minimumFractionDigits: params.minimumFractionDigits,
			maximumFractionDigits: params.maximumFractionDigits,
		})

		let returnedString = ''

		if (params.usePluralRule) {
			returnedString = $t('times [count $tc]', value)
		} else {
			returnedString = params.valueFormatterSuffix ? `${quantity} ${params.valueFormatterSuffix}` : quantity
		}

		return returnedString
	}

	const formatDateTime = (value, overriddenOptions = {}) => {
		return formatToFriendlyDateTime(value, overriddenOptions)
	}

	const formatDateTimeForPreview = (value, overriddenOptions = {}) => {
		const options = Object.assign({
			showSeconds: true,
		}, overriddenOptions)
		return formatDateTime(value, options)
	}

	const formatPeriod = (value, overriddenOptions = {}) => {
		return formatToFriendlyPeriod(value, overriddenOptions)
	}

	const formatPeriodForPreview = (value, overriddenOptions = {}) => {
		const options = Object.assign({}, overriddenOptions)
		return formatPeriod(value, options)
	}

	const formatDuration = (value, overriddenOptions = {}) => {
		const options = Object.assign({
			showDays: true,
		}, overriddenOptions)
		return makeDurationFormatterFromSeconds(value, options)
	}

	const formatFuelVolume = (value, overriddenParams) => {
		if (value === null) return null

		const params = Object.assign({
			minimumFractionDigits: 0,
			maximumFractionDigits: 2,
			valueFormatterSuffix: $t('l'),
			// isCoefficient: true,
		}, overriddenParams)

		const locales = getLocales()

		const liters = Number(value).toLocaleString(locales, {
			minimumFractionDigits: params.minimumFractionDigits,
			maximumFractionDigits: params.maximumFractionDigits,
		})

		return `${liters} ${params.valueFormatterSuffix}`
	}

	const formatSpeed = (value, overriddenParams) => {
		if (value === null) return null

		const params = Object.assign({
			minimumFractionDigits: 0,
			maximumFractionDigits: 0,
			valueFormatterSuffix: $t('km/h'),
			// isCoefficient: true,
		}, overriddenParams)

		const locales = getLocales()

		const speed = Number(value).toLocaleString(locales, {
			minimumFractionDigits: params.minimumFractionDigits,
			maximumFractionDigits: params.maximumFractionDigits,
		})

		return `${speed} ${params.valueFormatterSuffix}`
	}

	const formatWidth = (value, overriddenParams) => {
		const params = Object.assign({
			minimumFractionDigits: 0,
			maximumFractionDigits: 2,
			valueFormatterSuffix: $t('m'),
			// isCoefficient: true,
		}, overriddenParams)

		const locales = getLocales()

		const width = Number(value).toLocaleString(locales, {
			minimumFractionDigits: params.minimumFractionDigits,
			maximumFractionDigits: params.maximumFractionDigits,
		})

		return `${width} ${params.valueFormatterSuffix}`
	}

	const formatMaterial = (value, material, overriddenParams) => {
		const params = Object.assign({
			minimumFractionDigits: 0,
			maximumFractionDigits: 2,
		}, overriddenParams)

		const locales = getLocales()

		const materialValue = Number(value).toLocaleString(locales, {
			minimumFractionDigits: params.minimumFractionDigits,
			maximumFractionDigits: params.maximumFractionDigits,
		})

		return `${materialValue} ${material.measure}`
	}

	return {
		formatDefaultNumber,

		formatCurrency,
		formatPercent,
		formatPercentForPreview,
		formatArea,
		formatAreaForPreview,
		formatDistance,
		formatWeight,
		formatWeightForPreview,
		formatWeightDistance,
		formatQuantity,

		formatDateTime,
		formatDateTimeForPreview,
		formatPeriod,
		formatPeriodForPreview,
		formatDuration,

		formatFuelVolume,
		formatSpeed,
		formatWidth,

		formatMaterial,
	}
}
